import React, { createContext, useContext, useReducer, useCallback, ReactNode, useEffect } from 'react';
import {
  collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, where,
  Timestamp, FirestoreDataConverter, QueryDocumentSnapshot, SnapshotOptions,
  getDoc,
  setDoc
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '@/lib/firebase';
import { useBudgetManager } from './BudgetManagerContext';
import { toast } from 'sonner';

// Define types
export type LinkedAccount = {
  id?: string;
  name: string;
  balance: number;
  availableToBudget?: number;
  color: string;
  type: string;
  createdAt: Date;
  updatedAt: Date;
  createdBy?: string;
  budgetId?: string;
  accessToken?: string | null;
  externalId?: string | null;
  institution?: string | null;
  finicityCustomerId?: string | null;
};

export type AccountState = {
  accounts: LinkedAccount[];
  loading: boolean;
  error: string | null;
};

type AccountAction =
  | { type: 'LOAD_ACCOUNTS'; payload: LinkedAccount[] }
  | { type: 'ADD_ACCOUNT'; payload: LinkedAccount }
  | { type: 'UPDATE_ACCOUNT'; payload: LinkedAccount }
  | { type: 'REMOVE_ACCOUNT'; payload: string }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null };

type AccountContextType = {
  state: AccountState;
  loadAccounts: () => Promise<void>;
  addAccount: (account: Omit<LinkedAccount, 'id' | 'createdAt' | 'updatedAt' | 'budgetId'>) => Promise<void>;
  updateAccount: (account: LinkedAccount) => Promise<void>;
  removeAccount: (accountId: string) => Promise<void>;
  clearError: () => void;
};

const AccountContext = createContext<AccountContextType | undefined>(undefined);

const initialState: AccountState = {
  accounts: [],
  loading: false,
  error: null,
};

function accountReducer(state: AccountState, action: AccountAction): AccountState {
  switch (action.type) {
    case 'LOAD_ACCOUNTS':
      return { ...state, accounts: action.payload, loading: false };
    case 'ADD_ACCOUNT':
      return { ...state, accounts: [...state.accounts, action.payload], loading: false };
    case 'UPDATE_ACCOUNT':
      return {
        ...state,
        accounts: state.accounts.map((account) =>
          account.id === action.payload.id ? action.payload : account
        ),
        loading: false,
      };
    case 'REMOVE_ACCOUNT':
      return {
        ...state,
        accounts: state.accounts.filter((account) => account.id !== action.payload),
        loading: false,
      };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    default:
      return state;
  }
}

const linkedAccountConverter: FirestoreDataConverter<LinkedAccount> = {
  toFirestore: (account: LinkedAccount) => {
    return {
      ...account,
      createdAt: Timestamp.fromDate(account.createdAt),
      updatedAt: Timestamp.fromDate(account.updatedAt),
    };
  },
  fromFirestore: (
    snapshot: QueryDocumentSnapshot,
    options: SnapshotOptions
  ): LinkedAccount => {
    const data = snapshot.data(options);
    return {
      ...data,
      id: snapshot.id,
      createdAt: data.createdAt.toDate(),
      updatedAt: data.updatedAt.toDate(),
    } as LinkedAccount;
  },
};

export function AccountProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(accountReducer, initialState);
  const { state: budgetState } = useBudgetManager();

  const checkUserAccess = async (userId: string, currentBudget: any) => {
    if (!currentBudget.isShared) {
      return true;
    }

    const budgetDocRef = doc(db, 'shared_budgets', currentBudget.id);
    const budgetDocSnap = await getDoc(budgetDocRef);

    if (!budgetDocSnap.exists()) {
      throw new Error('Shared budget not found');
    }

    const data = budgetDocSnap.data();
    const sharedWith = data?.sharedWith || [];
    return sharedWith.includes(userId) || data?.ownerId === userId;
  };

  const loadAccounts = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const currentBudget = budgetState.currentBudget;
      if (!currentBudget || !currentBudget.id) {
        console.warn('No budget selected or budget has no ID');
        dispatch({ type: 'LOAD_ACCOUNTS', payload: [] });
        return;
      }

      const hasAccess = await checkUserAccess(user.uid, currentBudget);
      if (!hasAccess) {
        throw new Error('User does not have access to this budget');
      }

      const accountsQuery = query(
        collection(db, 'accounts'),
        where('budgetId', '==', currentBudget.id),
        where('createdBy', '==', user.uid)
      ).withConverter(linkedAccountConverter);

      const querySnapshot = await getDocs(accountsQuery);
      const accounts = querySnapshot.docs.map((doc) => doc.data());

      dispatch({ type: 'LOAD_ACCOUNTS', payload: accounts });
    } catch (error) {
      console.error('Error loading accounts:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load accounts' });
    }
  }, [budgetState.currentBudget]);

  const addAccount = async (account: Omit<LinkedAccount, 'id' | 'createdAt' | 'updatedAt'>): Promise<void> => {
    try {
      if (!auth.currentUser) {
        throw new Error('User not authenticated');
      }

      if (!budgetState.currentBudget) {
        throw new Error('No active budget selected');
      }

      // Sanitize account data to remove undefined values
      const sanitizedAccount = {
        ...account,
        institution: account.institution || null, // Convert undefined to null
        externalId: account.externalId || null,
        accessToken: account.accessToken || null,
        finicityCustomerId: account.finicityCustomerId || null
      };

      const accountsRef = collection(db, 'accounts').withConverter(accountConverter);
      const newAccount: LinkedAccount = {
        ...sanitizedAccount,
        createdBy: auth.currentUser.uid,
        budgetId: budgetState.currentBudget.id,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      const docRef = doc(accountsRef);
      await setDoc(docRef, newAccount);

      const savedAccount = { ...newAccount, id: docRef.id };
      dispatch({ type: 'ADD_ACCOUNT', payload: savedAccount });

      toast.success('Account added successfully');
    } catch (error) {
      console.error('Detailed account addition error:', error);
      toast.error('Failed to add account');
      throw error;
    }
  };

  // Update the Firestore converter to handle null values
  const accountConverter: FirestoreDataConverter<LinkedAccount> = {
    toFirestore: (account: LinkedAccount) => {
      const sanitizedAccount = { ...account };

      // Remove any undefined values
      Object.keys(sanitizedAccount).forEach(key => {
        if (sanitizedAccount[key as keyof LinkedAccount] === undefined) {
          (sanitizedAccount as any)[key] = null;
        }
      });

      return {
        ...sanitizedAccount,
        createdAt: Timestamp.fromDate(account.createdAt),
        updatedAt: Timestamp.fromDate(account.updatedAt),
      };
    },
    fromFirestore: (
      snapshot: QueryDocumentSnapshot,
      options: SnapshotOptions
    ): LinkedAccount => {
      const data = snapshot.data(options);
      return {
        ...data,
        id: snapshot.id,
        createdAt: data.createdAt.toDate(),
        updatedAt: data.updatedAt.toDate(),
        // Convert null back to undefined if needed
        institution: data.institution || undefined,
        externalId: data.externalId || undefined,
        accessToken: data.accessToken || undefined,
        finicityCustomerId: data.finicityCustomerId || undefined
      } as LinkedAccount;
    },
  };

  const updateAccount = useCallback(async (account: LinkedAccount) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');
      const currentBudget = budgetState.currentBudget;
      if (!currentBudget) throw new Error('No budget selected');

      const hasAccess = await checkUserAccess(user.uid, currentBudget);
      if (!hasAccess) {
        throw new Error('User does not have access to this budget');
      }

      if (!account.id) throw new Error('Account ID is required for update');

      const { id, ...updateData } = account;
      updateData.updatedAt = new Date();

      const accountRef = doc(db, 'accounts', id).withConverter(linkedAccountConverter);
      await updateDoc(accountRef, updateData);

      dispatch({ type: 'UPDATE_ACCOUNT', payload: account });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to update account' });
    }
  }, [budgetState.currentBudget]);

  const removeAccount = useCallback(async (accountId: string) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');
      const currentBudget = budgetState.currentBudget;
      if (!currentBudget) throw new Error('No budget selected');

      const hasAccess = await checkUserAccess(user.uid, currentBudget);
      if (!hasAccess) {
        throw new Error('User does not have access to this budget');
      }

      await deleteDoc(doc(db, 'accounts', accountId));
      dispatch({ type: 'REMOVE_ACCOUNT', payload: accountId });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to remove account' });
    }
  }, [budgetState.currentBudget]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        loadAccounts();
      } else {
        dispatch({ type: 'LOAD_ACCOUNTS', payload: [] });
      }
    });

    return () => unsubscribe();
  }, [loadAccounts]);

  const clearError = useCallback(() => {
    dispatch({ type: 'SET_ERROR', payload: null });
  }, []);

  return (
    <AccountContext.Provider
      value={{
        state,
        loadAccounts,
        addAccount,
        updateAccount,
        removeAccount,
        clearError,
      }}
    >
      {children}
    </AccountContext.Provider>
  );
}

export function useAccount(): AccountContextType {
  const context = useContext(AccountContext);
  if (!context) {
    throw new Error('useAccount must be used within an AccountProvider');
  }
  return context;
}